import { useEffect, useState } from "react";
import { api, apiError, GBP, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { CreditCard, Download } from "lucide-react";

function PayStatus({ s }) {
  const map = { PAID: "#34D399", DUE: "#D4AF37", OVERDUE: "#F59E0B", FAILED: "#F87171" };
  return <span className="text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-sm" style={{ color: map[s] || "#8C867A", background: `${map[s] || "#8C867A"}18` }}>{s}</span>;
}

export default function Membership() {
  const [d, setD] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => { api.get("/membership/me").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[#8C867A]">Loading…</div>;
  const m = d.member;
  const nextDue = d.payments.find((p) => p.status === "DUE" || p.status === "OVERDUE");

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "bench_membership_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setPaying(false); }
  };

  const openReceipt = (p) => {
    const w = window.open("", "_blank", "width=480,height=680");
    if (!w) return toast.error("Allow pop-ups to view the receipt");
    w.document.write(`<!doctype html><html><head><title>3B LDN Receipt</title><meta name=viewport content="width=device-width,initial-scale=1"><style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;background:#0D0D0E;color:#F5F3EF;margin:0;padding:40px}h1{font-family:Georgia,serif;letter-spacing:.04em;margin:6px 0 20px}.b{color:#C5A059}.row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid #28282D;font-size:14px}.muted{color:#8C867A;font-size:11px;text-transform:uppercase;letter-spacing:.2em}.tot{font-size:22px;font-family:Georgia,serif}@media print{body{background:#fff;color:#000}.row{border-color:#ddd}.b{color:#8a6d2f}}</style></head><body>
    <div class="muted">3B LDN · Bonded by Bullion · Hatton Garden, London</div>
    <h1>Payment Receipt</h1>
    <div class="row"><span class="muted">Member</span><span>${m.name}</span></div>
    <div class="row"><span class="muted">Bench</span><span>${m.bench_number || "—"}</span></div>
    <div class="row"><span class="muted">Type</span><span>${p.type}</span></div>
    <div class="row"><span class="muted">Period</span><span>${p.period}</span></div>
    <div class="row"><span class="muted">Method</span><span>${p.method || "—"}</span></div>
    <div class="row"><span class="muted">Status</span><span>${p.status}</span></div>
    <div class="row"><span class="muted">Date</span><span>${p.paid_at ? new Date(p.paid_at).toLocaleDateString("en-GB") : "—"}</span></div>
    <div class="row"><span class="muted">Amount paid</span><span class="tot b">${GBP(p.amount)}</span></div>
    <p class="muted" style="margin-top:24px;line-height:1.6">This receipt relates to bench membership. Security deposits are held in trust as a refundable liability and are not membership revenue.</p>
    <button onclick="window.print()" style="margin-top:16px;background:linear-gradient(90deg,#C5A059,#D4AF37);border:0;color:#0D0D0E;font-weight:600;padding:12px 20px;border-radius:4px;cursor:pointer">Print / Save as PDF</button>
    </body></html>`);
    w.document.close();
  };

  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow={`${m.bench_number} · Hatton Garden`} title="My Membership" />

      <div className="grid sm:grid-cols-3 gap-4 mb-4 rise">
        <div className="panel rounded-sm p-5">
          <div className="eyebrow mb-2">Status</div>
          <div className="font-serif text-3xl" style={{ color: "#34D399" }}>{m.status}</div>
          <div className="text-xs text-[#8C867A] mt-1">Standard Bench Membership</div>
        </div>
        <div className="panel rounded-sm p-5">
          <div className="eyebrow mb-2">Monthly Fee</div>
          <div className="font-serif text-3xl brass-text">{GBP(m.monthly_fee)}</div>
          <div className="text-xs text-[#8C867A] mt-1">£100/week equivalent</div>
        </div>
        <div className="panel rounded-sm p-5">
          <div className="eyebrow mb-2">Next Payment</div>
          <div className="font-serif text-2xl text-[#F5F3EF]">{fmtDate(m.next_payment_date)}</div>
        </div>
      </div>

      <div className="panel rounded-sm p-5 mb-4 flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "#3E382A" }}>
        <div>
          <div className="eyebrow mb-1">Security Deposit — Held in Trust</div>
          <div className="font-serif text-2xl brass-text">{GBP(m.security_deposit)}</div>
          <div className="text-[0.65rem] text-[#8C867A] mt-1">Refundable contribution · not a membership charge</div>
        </div>
        <div className="text-right">
          {nextDue ? (
            <button onClick={payNow} disabled={paying} data-testid="pay-now" className="btn-brass flex items-center gap-2 px-5 py-3 rounded-sm text-sm">
              <CreditCard size={16} /> {paying ? "Redirecting…" : `Pay ${GBP(nextDue.amount)} Now`}
            </button>
          ) : <div className="text-sm" style={{ color: "#34D399" }}>All payments up to date</div>}
          <div className="text-[0.6rem] text-[#8C867A] mt-2">Secure checkout via Stripe · test mode</div>
        </div>
      </div>

      <div className="panel rounded-sm p-5">
        <div className="eyebrow mb-4">Payment History</div>
        <div className="divide-y" style={{ borderColor: "#28282D" }}>
          {d.payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-3 text-sm">
              <div><div className="text-[#E2DFD7]">{p.period}</div><div className="text-[0.65rem] text-[#8C867A]">{p.type} · {p.method}</div></div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[#E2DFD7]">{GBP(p.amount)}</span>
                <PayStatus s={p.status} />
                {p.status === "PAID" && <button onClick={() => openReceipt(p)} data-testid={`receipt-${p.id}`} className="text-[#8C867A] hover:brass-text" title="View / download receipt"><Download size={14} /></button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
